var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import * as moment from 'moment';
import { AddressModel } from './address';
import { BaseEntity, Default, MemoizeGetters, Transform, toDate, toEntity, } from './base';
import { DeliveryLocation } from './delivery-location';
import { EtaModel } from './eta';
import { OpsUserModel } from './user.ops';
import { OpsVehicleModel } from './vehicle.ops';
import { ServiceModel } from './service';
import { ShiftModel } from './shift';
export var OrderStatus;
(function (OrderStatus) {
    OrderStatus["Pending"] = "pending";
    OrderStatus["Claimed"] = "claimed";
    OrderStatus["Pumping"] = "pumping";
    OrderStatus["Complete"] = "complete";
    OrderStatus["Cancelled"] = "cancelled";
    OrderStatus["Proposed"] = "proposed";
    OrderStatus["Rejected"] = "rejected";
    OrderStatus["Deleted"] = "deleted";
    OrderStatus["MissingPayment"] = "missing_valid_card";
})(OrderStatus || (OrderStatus = {}));
export var OnTimeStatus;
(function (OnTimeStatus) {
    OnTimeStatus["Early"] = "early";
    OnTimeStatus["Available"] = "available";
    OnTimeStatus["Late"] = "late";
    OnTimeStatus["Priority"] = "priority";
    OnTimeStatus["AtRisk"] = "at-risk";
})(OnTimeStatus || (OnTimeStatus = {}));
export var OrderCancelReason;
(function (OrderCancelReason) {
    OrderCancelReason["OwnerMissing"] = "owner_missing";
    OrderCancelReason["Placeholder"] = "placeholder";
})(OrderCancelReason || (OrderCancelReason = {}));
let OpsOrderModel = class OpsOrderModel extends BaseEntity {
    constructor(json) {
        super(json);
        this.AT_RISK_HOUR_THRESHOLD = 1;
        this.PRIORITY_HOUR_THRESHOLD = 4;
        this.shiftStartTime =
            json.shift_start_time || ShiftModel.defaultStartTime(this.shiftWindow);
        this.shiftEndTime =
            json.shift_end_time || ShiftModel.defaultEndTime(this.shiftWindow);
    }
    get completedOnTime() {
        if (!this.timeCompleted) {
            return false;
        }
        return (this.timeWindowStart.isBefore(this.timeCompleted) &&
            this.timeWindowEnd.isAfter(this.timeCompleted));
    }
    currentOnTimeStatus() {
        const now = moment();
        const startTime = this.timeWindowStart;
        const endTime = this.timeWindowEnd;
        if (!startTime || !endTime) {
            return OnTimeStatus.Late;
        }
        if (now.isBefore(startTime)) {
            return OnTimeStatus.Early;
        }
        if (now.isAfter(endTime)) {
            return OnTimeStatus.Late;
        }
        if (endTime.diff(now, 'seconds') <= this.AT_RISK_HOUR_THRESHOLD * 3600) {
            return OnTimeStatus.AtRisk;
        }
        if (endTime.diff(now, 'seconds') <= this.PRIORITY_HOUR_THRESHOLD * 3600) {
            return OnTimeStatus.Priority;
        }
        if (now.isBetween(startTime, endTime)) {
            return OnTimeStatus.Available;
        }
        return OnTimeStatus.Late;
    }
    get timeWindowString() {
        if (this.slotStartTime && this.slotEndTime) {
            const options = {
                hour: 'numeric',
                minute: '2-digit',
            };
            const startTime = this.slotStartTime.toLocaleTimeString('en-us', options);
            const endTime = this.slotEndTime.toLocaleTimeString('en-us', options);
            const timeZone = new Date()
                .toLocaleTimeString('en-us', { timeZoneName: 'short' })
                .split(' ')[2];
            return `${startTime}-${endTime} (${timeZone})`;
        }
        return `${this.timeWindowStart.format('h:mmA')}-${this.timeWindowEnd.format('h:mmA')}`;
    }
    get timeWindowStart() {
        const shiftStartTime = moment(this.date);
        shiftStartTime.set('hour', this.shiftStartTime.hour);
        shiftStartTime.set('minutes', this.shiftStartTime.minute);
        return shiftStartTime;
    }
    get timeWindowEnd() {
        let end;
        const shiftEndTime = new Date();
        shiftEndTime.setHours(this.shiftEndTime.hour);
        shiftEndTime.setMinutes(this.shiftEndTime.minute);
        end = moment(shiftEndTime);
        return end.isBefore(this.timeWindowStart) ? end.add(1, 'day') : end;
    }
    dayOfTheWeek(relative = true) {
        let allDays = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
        ];
        if (relative) {
            if (this.isToday) {
                return 'today';
            }
            if (this.isTomorrow) {
                return 'tomorrow';
            }
        }
        return allDays[this.date.getDay()];
    }
    get relativeDate() {
        if (this.isToday) {
            return 'today';
        }
        if (this.isTomorrow) {
            return 'tomorrow';
        }
        let momentDate = moment(this.date);
        return momentDate.format('dddd MMMM Do');
    }
    /**
     * Useful to check if drivers are trying to claim/complete an order at the wrong time.
     * @returns {boolean} true if the current time is too far outside of the eta window
     */
    outsideTargetTimeWindow(date) {
        const ETA_HOUR_WARNING_LIMIT = 0.5;
        return (this.timeWindowStart.hour() - moment(date).hour() >=
            ETA_HOUR_WARNING_LIMIT);
    }
    get etaString() {
        if (this.pumpingEta) {
            let momentDate = moment(this.pumpingEta);
            return momentDate.format('h:mmA');
        }
    }
    get isToday() {
        return new Date().toDateString() == this.date.toDateString();
    }
    get isTomorrow() {
        let tomorrow = new Date();
        tomorrow.setDate(new Date().getDate() + 1);
        return tomorrow.toDateString() == this.date.toDateString();
    }
    get hasDeliveryCoordinates() {
        return (this.deliveryLocation &&
            this.deliveryLocation.lat != null &&
            this.deliveryLocation.lng != null);
    }
};
__decorate([
    Transform(toDate),
    __metadata("design:type", Date)
], OpsOrderModel.prototype, "slotStartTime", void 0);
__decorate([
    Transform(toDate),
    __metadata("design:type", Date)
], OpsOrderModel.prototype, "slotEndTime", void 0);
__decorate([
    Transform(toDate),
    __metadata("design:type", Date)
], OpsOrderModel.prototype, "date", void 0);
__decorate([
    Transform(toDate),
    __metadata("design:type", Date)
], OpsOrderModel.prototype, "pumpingEta", void 0);
__decorate([
    Transform(toDate),
    __metadata("design:type", Date)
], OpsOrderModel.prototype, "timeCompleted", void 0);
__decorate([
    Transform(toEntity(EtaModel)),
    __metadata("design:type", EtaModel)
], OpsOrderModel.prototype, "firstEta", void 0);
__decorate([
    Transform(toEntity(AddressModel)),
    __metadata("design:type", AddressModel)
], OpsOrderModel.prototype, "address", void 0);
__decorate([
    Transform(toEntity(OpsVehicleModel)),
    __metadata("design:type", OpsVehicleModel)
], OpsOrderModel.prototype, "vehicle", void 0);
__decorate([
    Transform(toEntity(OpsUserModel)),
    __metadata("design:type", OpsUserModel)
], OpsOrderModel.prototype, "user", void 0);
__decorate([
    Transform(toEntity(DeliveryLocation)),
    __metadata("design:type", DeliveryLocation)
], OpsOrderModel.prototype, "deliveryLocation", void 0);
__decorate([
    Transform(toEntity(ServiceModel)),
    __metadata("design:type", ServiceModel)
], OpsOrderModel.prototype, "service", void 0);
__decorate([
    Default([]),
    __metadata("design:type", Array)
], OpsOrderModel.prototype, "orderItems", void 0);
OpsOrderModel = __decorate([
    MemoizeGetters,
    __metadata("design:paramtypes", [Object])
], OpsOrderModel);
export { OpsOrderModel };
//# sourceMappingURL=order.ops.js.map


// WEBPACK FOOTER //
// ./src/models/order.ops.ts.pre-build-optimizer.js